import { useEffect, useState } from "react";
import type { ComponentType } from "react";
import { useLocation, useParams } from "react-router";
import type { To } from "react-router";
import { DetailStackLevelContext, DetailStackSharedContext } from "./detailStack.context";
import type { DetailStackCollapse, DetailStackLevel, DetailStackTopOffset } from "./detailStack.context";
import { DETAIL_STACK_WIDTH, toDetailStackEntryList, toDetailStackPath } from "./detailStack.utils";
import type { DetailStackEntry, DetailStackKindArity, DetailStackSize } from "./detailStack.utils";

/**
 * A kind's drawer: a bare component takes no id (URL `kind`); `{ component, hasId: true }` shows
 * a record (URL `kind/id`). `size` is `wide` (default) or `compact`.
 */
export type DetailStackDrawer = ComponentType | { component: ComponentType; hasId?: boolean; size?: DetailStackSize };

const toComponent = (drawer: DetailStackDrawer): ComponentType => {
  if ("component" in drawer) {
    return drawer.component;
  }
  return drawer;
};

const toHasId = (drawer: DetailStackDrawer): boolean => {
  if ("component" in drawer) {
    return drawer.hasId ?? false;
  }
  return false;
};

const toSize = (drawer: DetailStackDrawer): DetailStackSize => {
  if ("component" in drawer) {
    return drawer.size ?? "wide";
  }
  return "wide";
};

interface DetailStackProps<K extends string> {
  /** One drawer per kind; the keys are the kind names in the URL. */
  drawers: Record<K, DetailStackDrawer>;
  /** The query string every stack link keeps, so closing restores the page. Default: the router's. */
  getSearch?: () => string;
  /** How far each drawer underneath shifts left per level above it, in px. Default 32. */
  offsetPx?: number;
  /** How far below the top of the window drawers start. Default 0. */
  topOffset?: DetailStackTopOffset;
}

/**
 * Drawers opened from drawers, as deep as you like, all in the URL: the element of a catch-all
 * route under a page. The path after the page is read as `kind/id` entries, each rendered by
 * `drawers[kind]` (a prop-less component that reads its level and renders a DetailDrawer).
 *
 *   { path: "list", element: <Page />, children: [{ path: "*", element: <DetailStack drawers={…} /> }] }
 */
export const DetailStack = <K extends string>(props: DetailStackProps<K>) => {
  const { drawers, getSearch, offsetPx = 32, topOffset = 0 } = props;
  const params = useParams();
  const location = useLocation();
  const [collapse, setCollapse] = useState<DetailStackCollapse | null>(null);
  const kinds = Object.keys(drawers) as K[];
  const hasIdByKind = {} as DetailStackKindArity<K>;
  for (const kind of kinds) hasIdByKind[kind] = toHasId(drawers[kind]);
  const splat = params["*"] ?? "";
  const entries = toDetailStackEntryList(splat, hasIdByKind);
  let base = location.pathname;
  if (splat && base.endsWith(splat)) base = base.slice(0, base.length - splat.length);
  base = base.replace(/\/$/, "");
  let search = location.search;
  if (getSearch) search = getSearch();
  const shared = { collapse, topOffset };

  useEffect(() => {
    setCollapse(null);
  }, [location.pathname]);

  const levels = entries.map((entry, depth) => {
    const own = entries.slice(0, depth + 1);
    const closeTo: To = { pathname: toDetailStackPath(base, entries.slice(0, depth)), search };
    const selfTo: To = { pathname: toDetailStackPath(base, own), search };
    const level: DetailStackLevel<K> = {
      kind: entry.kind,
      id: entry.id,
      depth,
      size: entries.length,
      isTop: depth === entries.length - 1,
      isBottom: depth === 0,
      width: DETAIL_STACK_WIDTH[toSize(drawers[entry.kind])],
      offsetPx: (entries.length - 1 - depth) * offsetPx,
      closeTo,
      selfTo,
      toChild: (kind, id) => {
        if (hasIdByKind[kind] !== (id !== undefined)) {
          throw new Error(`detail stack: "${kind}" ${hasIdByKind[kind] ? "needs an id" : "takes no id"}`);
        }
        const child: DetailStackEntry<K> = { kind, id: id ?? null };
        return { pathname: toDetailStackPath(base, [...own, child]), search };
      },
      toSwap: (kind, id) => {
        if (hasIdByKind[kind] !== (id !== undefined)) {
          throw new Error(`detail stack: "${kind}" ${hasIdByKind[kind] ? "needs an id" : "takes no id"}`);
        }
        const swapped: DetailStackEntry<K> = { kind, id: id ?? null };
        return { pathname: toDetailStackPath(base, [...entries.slice(0, depth), swapped]), search };
      },
      close: () => setCollapse({ depth: depth - 1, to: closeTo }),
      focus: () => setCollapse({ depth, to: selfTo }),
    };
    const Drawer = toComponent(drawers[entry.kind]);
    const untypedLevel = level as unknown as DetailStackLevel;

    return (
      <DetailStackLevelContext.Provider
        key={`${depth}:${entry.kind}:${entry.id ?? ""}`}
        value={untypedLevel}
      >
        <Drawer />
      </DetailStackLevelContext.Provider>
    );
  });

  return (
    <DetailStackSharedContext.Provider value={shared}>
      {levels}
    </DetailStackSharedContext.Provider>
  );
};
